import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Waves, Compass, CalendarCheck, Headset, ChevronDown } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "../components/BookingDialog";
import ActivityCard from "../components/ActivityCard";
import { ACTIVITIES, TESTIMONIALS } from "../data/mock";

const HERO_IMG = "https://images.pexels.com/photos/30557503/pexels-photo-30557503.jpeg";

const Reveal = ({ children, delay = 0 }) => {
  const [ref, setRef] = useState(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!ref) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setShown(true),
      { threshold: 0.12 }
    );
    obs.observe(ref);
    return () => obs.disconnect();
  }, [ref]);
  return (
    <div
      ref={setRef}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-[900ms] ease-out ${
        shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      {children}
    </div>
  );
};

const Home = () => {
  const { t, lang } = useLang();
  const { openBooking } = useBooking();
  const whyIcons = [Waves, Compass, CalendarCheck, Headset];
  const popular = ACTIVITIES.filter((a) => a.popular).slice(0, 6);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Agadir" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1c221e]/70 via-[#1c221e]/35 to-[#1c221e]/75" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 w-full pt-24">
          <div className="max-w-2xl">
            <p className="text-white/85 text-xs md:text-sm tracking-[0.25em] uppercase mb-6 animate-[fadeUp_1s_ease]">
              {t.hero.eyebrow}
            </p>
            <h1 className="font-serif text-white text-[3.2rem] leading-[1.02] sm:text-6xl md:text-7xl whitespace-pre-line mb-7 animate-[fadeUp_1.1s_ease]">
              {t.hero.title}
            </h1>
            <p className="text-white/85 text-base md:text-lg leading-relaxed max-w-xl mb-10 animate-[fadeUp_1.2s_ease]">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-[fadeUp_1.3s_ease]">
              <Link
                to="/activites"
                className="inline-flex items-center justify-center gap-2 bg-[#7c8f74] hover:bg-[#697c62] text-white px-8 py-4 rounded-full text-sm tracking-[0.1em] uppercase font-medium transition-all hover:-translate-y-0.5 hover:shadow-xl"
              >
                {t.hero.cta} <ArrowRight size={17} />
              </Link>
              <button
                onClick={() => openBooking()}
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-md border border-white/40 text-white px-8 py-4 rounded-full text-sm tracking-[0.1em] uppercase font-medium transition-all hover:bg-white/20"
              >
                {t.nav.book}
              </button>
            </div>

            <div className="flex gap-8 md:gap-12 mt-14 animate-[fadeUp_1.4s_ease]">
              {[
                { n: `${ACTIVITIES.length}`, l: t.hero.stat1 },
                { n: "100%", l: t.hero.stat2 },
                { n: "24/7", l: t.hero.stat3 },
              ].map((s, i) => (
                <div key={i}>
                  <p className="font-serif text-3xl md:text-4xl text-white">{s.n}</p>
                  <p className="text-white/70 text-xs tracking-wide mt-1 max-w-[7rem]">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 animate-bounce">
          <ChevronDown size={26} />
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 md:py-32 bg-[#f7f5ef]">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <Reveal>
            <p className="text-[#7c8f74] text-xs tracking-[0.25em] uppercase mb-5">{t.intro.eyebrow}</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#20261f] leading-tight mb-7">
              {t.intro.title}
            </h2>
            <p className="text-[#6b7166] text-lg leading-relaxed">{t.intro.text}</p>
          </Reveal>
        </div>
      </section>

      {/* POPULAR ACTIVITIES */}
      <section className="pb-24 md:pb-32 bg-[#f7f5ef]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div>
                <p className="text-[#7c8f74] text-xs tracking-[0.25em] uppercase mb-4">{t.activities.eyebrow}</p>
                <h2 className="font-serif text-4xl md:text-5xl text-[#20261f]">{t.activities.title}</h2>
                <p className="text-[#6b7166] mt-4 max-w-lg">{t.activities.subtitle}</p>
              </div>
              <Link
                to="/activites"
                className="hidden md:inline-flex items-center gap-2 text-sm tracking-[0.1em] uppercase font-medium text-[#5c6b54] hover:gap-3 transition-all whitespace-nowrap"
              >
                {t.activities.viewAll} <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {popular.map((a, i) => (
              <Reveal key={a.id} delay={(i % 3) * 100}>
                <ActivityCard activity={a} />
              </Reveal>
            ))}
          </div>

          <div className="mt-10 text-center md:hidden">
            <Link
              to="/activites"
              className="inline-flex items-center gap-2 text-sm tracking-[0.1em] uppercase font-medium text-[#5c6b54]"
            >
              {t.activities.viewAll} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-24 md:py-28 bg-[#eef0e9]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-[#7c8f74] text-xs tracking-[0.25em] uppercase mb-4">{t.why.eyebrow}</p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#20261f]">{t.why.title}</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.why.items.map((item, i) => {
              const Icon = whyIcons[i];
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="text-center px-2">
                    <div className="w-16 h-16 rounded-full bg-[#7c8f74]/12 flex items-center justify-center mx-auto mb-6">
                      <Icon size={26} className="text-[#5c6b54]" strokeWidth={1.6} />
                    </div>
                    <h3 className="font-serif text-xl text-[#20261f] mb-3">{item.title}</h3>
                    <p className="text-sm text-[#6b7166] leading-relaxed">{item.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-[#f7f5ef]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-[#7c8f74] text-xs tracking-[0.25em] uppercase mb-4">{t.testimonials.eyebrow}</p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#20261f]">{t.testimonials.title}</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {TESTIMONIALS.map((tm, i) => (
              <Reveal key={tm.id} delay={i * 100}>
                <div className="bg-white rounded-2xl p-8 border border-[#eceae1] h-full flex flex-col">
                  <div className="flex gap-1 mb-5">
                    {Array.from({ length: tm.rating }).map((_, j) => (
                      <Star key={j} size={16} className="fill-[#c8a44d] text-[#c8a44d]" />
                    ))}
                  </div>
                  <p className="text-[#4a5045] leading-relaxed italic mb-6 flex-1">"{tm.text[lang]}"</p>
                  <div>
                    <p className="font-medium text-[#20261f]">{tm.name}</p>
                    <p className="text-xs text-[#9aa08f] tracking-wide">{tm.country[lang]}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[#1c221e]" />
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl text-white mb-5 leading-tight">{t.ctaBand.title}</h2>
            <p className="text-white/70 text-lg mb-10">{t.ctaBand.text}</p>
            <button
              onClick={() => openBooking()}
              className="inline-flex items-center gap-2 bg-[#7c8f74] hover:bg-[#8ba081] text-white px-10 py-4 rounded-full text-sm tracking-[0.1em] uppercase font-medium transition-all hover:-translate-y-0.5 hover:shadow-xl"
            >
              {t.ctaBand.button} <ArrowRight size={17} />
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
